import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API, BACKEND_URL } from '@/App';
import { toast } from 'sonner';
import {
  Plus, Edit, Trash2, Eye, EyeOff, RefreshCw,
  Upload, Image as ImageIcon, Sparkles, FileText, GripVertical,
  Shield, Users, AlertCircle, Car, DollarSign, X, Check, Download, FileSpreadsheet
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// Allowed features
const ALLOWED_FEATURES = ['4WD/AWD', 'Backup Camera', 'Apple CarPlay', 'Android Auto', 'Bluetooth Radio'];

// Body type options
const BODY_TYPE_OPTIONS = ['Sedan', 'SUV', 'Truck', 'Van', 'Coupe', 'Convertible', 'Hatchback', 'Wagon', 'Crossover', 'Minivan', 'Pickup'];

// Title status options
const TITLE_STATUS_OPTIONS = ['Clean', 'Salvage', 'Rebuilt', 'Flood', 'Lemon'];
const ACCIDENT_OPTIONS = ['0 Accidents', '1 Accident', '2 Accidents', '3+ Accidents'];
const OWNER_OPTIONS = ['1 Owner', '2 Owners', '3 Owners', '4+ Owners'];
const USE_TYPE_OPTIONS = ['Personal', 'Fleet', 'Corporate', 'Rental', 'Lease'];

const InventoryManagement = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [generatingDesc, setGeneratingDesc] = useState(false);
  const [generatingSeo, setGeneratingSeo] = useState(false);
  const [uploadingCarfax, setUploadingCarfax] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const fileInputRef = useRef(null);
  const carfaxInputRef = useRef(null);
  const csvInputRef = useRef(null);
  
  // CarGurus Import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importUrl, setImportUrl] = useState('https://www.cargurus.com/Cars/m-We-Have-Your-Car-sp461210');
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [importMode, setImportMode] = useState('csv'); // 'csv' or 'scrape'
  
  const [formData, setFormData] = useState({
    year: '',
    make: '',
    model: '',
    trim: '',
    price: '',
    mileage: '',
    body_type: '',
    drivetrain: '',
    engine: '',
    transmission: '',
    exterior_color: '',
    interior_color: '',
    fuel_type: 'Gasoline',
    vin: '',
    stock_number: '',
    description: '',
    ai_description: '',
    images: [],
    features: [],
    is_active: true,
    is_featured: false,
    is_sold: false,
    carfax_url: '',
    carfax_file: '',
    show_carfax: false,
    show_calculator: true,
    // Vehicle History
    title_status: '',
    accidents: '',
    previous_owners: '',
    use_type: '',
    // SEO
    seo_title: '',
    seo_description: ''
  });

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/vehicles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVehicles(response.data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    } finally {
      setLoading(false);
    }
  };

  // CarGurus Import Handler
  const handleImportCarGurus = async () => {
    setImporting(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${API}/admin/import-cargurus`, {
        dealer_url: importUrl,
        replace_existing: replaceExisting
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { imported, updated, skipped, total_found } = response.data;
      toast.success(`Import complete! Added: ${imported}, Updated: ${updated}, Skipped: ${skipped} (Total found: ${total_found})`);
      setShowImportModal(false);
      fetchVehicles();
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to import from CarGurus: ' + (error.response?.data?.detail || error.message));
    } finally {
      setImporting(false);
    }
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    try {
      const token = localStorage.getItem('adminToken');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('replace_existing', replaceExisting);
      
      const response = await axios.post(`${API}/admin/import-csv`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const { imported, updated, skipped, errors } = response.data;
      toast.success(`CSV Import complete! Added: ${imported}, Updated: ${updated}, Skipped: ${skipped}${errors > 0 ? `, Errors: ${errors}` : ''}`);
      setShowImportModal(false);
      fetchVehicles();
    } catch (error) {
      console.error('CSV import error:', error);
      toast.error('Failed to import CSV: ' + (error.response?.data?.detail || error.message));
    } finally {
      setImporting(false);
      if (csvInputRef.current) {
        csvInputRef.current.value = '';
      }
    }
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const formDataUpload = new FormData();
      
      for (let i = 0; i < files.length; i++) {
        formDataUpload.append('files', files[i]);
      }
      
      const response = await axios.post(`${API}/admin/upload-images`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Cloudinary returns full URLs, use them directly
      const newUrls = response.data.urls;
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...newUrls]
      }));
      toast.success(`${response.data.count} image(s) uploaded successfully!`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload images');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleCarfaxUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!editingVehicle?.id) {
      toast.error('Please save the vehicle first before uploading CARFAX');
      return;
    }
    
    setUploadingCarfax(true);
    try {
      const token = localStorage.getItem('adminToken');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axios.post(
        `${API}/admin/upload-carfax?vehicle_id=${editingVehicle.id}`, 
        formDataUpload, 
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setFormData(prev => ({ 
        ...prev, 
        carfax_file: BACKEND_URL + response.data.url,
        show_carfax: true
      }));
      toast.success('CARFAX uploaded successfully!');
    } catch (error) {
      console.error('CARFAX upload error:', error);
      toast.error('Failed to upload CARFAX');
    } finally {
      setUploadingCarfax(false);
      if (carfaxInputRef.current) {
        carfaxInputRef.current.value = '';
      }
    }
  };

  const handleGenerateDescription = async () => {
    if (!formData.year || !formData.make || !formData.model || !formData.price || !formData.mileage) {
      toast.error('Please fill in Year, Make, Model, Price, and Mileage first');
      return;
    }
    
    setGeneratingDesc(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${API}/admin/generate-description`, {
        year: parseInt(formData.year),
        make: formData.make,
        model: formData.model,
        trim: formData.trim,
        mileage: parseInt(formData.mileage),
        price: parseFloat(formData.price),
        body_type: formData.body_type,
        drivetrain: formData.drivetrain,
        engine: formData.engine,
        transmission: formData.transmission,
        exterior_color: formData.exterior_color,
        interior_color: formData.interior_color,
        features: formData.features,
        // Include vehicle history
        title_status: formData.title_status,
        accidents: formData.accidents,
        previous_owners: formData.previous_owners,
        use_type: formData.use_type
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFormData(prev => ({ ...prev, ai_description: response.data.description, description: response.data.description }));
      toast.success('AI description generated and applied!');
    } catch (error) {
      console.error('AI generation error:', error);
      toast.error('Failed to generate description. Please try again.');
    } finally {
      setGeneratingDesc(false);
    }
  };

  const generateSeo = async () => {
    if (!formData.year || !formData.make || !formData.model || !formData.price || !formData.mileage) {
      toast.error('Please fill in Year, Make, Model, Price, and Mileage first');
      return;
    }
    
    setGeneratingSeo(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${API}/admin/generate-seo`, {
        year: parseInt(formData.year),
        make: formData.make,
        model: formData.model,
        trim: formData.trim,
        mileage: parseInt(formData.mileage),
        price: parseFloat(formData.price),
        body_type: formData.body_type,
        drivetrain: formData.drivetrain,
        features: formData.features
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFormData(prev => ({ 
        ...prev, 
        seo_title: response.data.seo_title,
        seo_description: response.data.seo_description 
      }));
      toast.success('SEO content generated by We Have Your Car AI Tool!');
    } catch (error) {
      console.error('AI SEO generation error:', error);
      toast.error('Failed to generate SEO. Please try again.');
    } finally {
      setGeneratingSeo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('adminToken');
    
    try {
      // Map feature names to what backend expects
      const mappedFeatures = formData.features.map(f => {
        if (f === '4WD/AWD') return '4WD';
        return f;
      });
      
      const submitData = {
        ...formData,
        year: parseInt(formData.year),
        price: parseFloat(formData.price),
        mileage: parseInt(formData.mileage),
        mpg_city: formData.mpg_city ? parseInt(formData.mpg_city) : 0,
        mpg_highway: formData.mpg_highway ? parseInt(formData.mpg_highway) : 0,
        features: mappedFeatures
      };

      if (editingVehicle) {
        await axios.put(`${API}/vehicles/${editingVehicle.id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Vehicle updated');
      } else {
        await axios.post(`${API}/vehicles`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Vehicle added');
      }
      
      setShowForm(false);
      setEditingVehicle(null);
      resetForm();
      fetchVehicles();
    } catch (error) {
      toast.error('Failed to save vehicle');
    }
  };

  const resetForm = () => {
    setFormData({
      year: '',
      make: '',
      model: '',
      trim: '',
      price: '',
      mileage: '',
      body_type: '',
      drivetrain: '',
      engine: '',
      transmission: '',
      exterior_color: '',
      interior_color: '',
      fuel_type: 'Gasoline',
      vin: '',
      stock_number: '',
      description: '',
      ai_description: '',
      images: [],
      features: [],
      is_active: true,
      is_featured: false,
      is_sold: false,
      carfax_url: '',
      carfax_file: '',
      show_carfax: false,
      show_calculator: true,
      title_status: '',
      accidents: '',
      previous_owners: '',
      use_type: '',
      seo_title: '',
      seo_description: ''
    });
  };

  const handleEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    // Map features back to display names
    const displayFeatures = (vehicle.features || []).map(f => {
      if (f === '4WD' || f === 'AWD') return '4WD/AWD';
      return f;
    });
    setFormData({
      ...vehicle,
      images: vehicle.images || [],
      features: displayFeatures
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this vehicle?')) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/vehicles/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Vehicle deleted');
      fetchVehicles();
    } catch (error) {
      toast.error('Failed to delete vehicle');
    }
  };

  const toggleActive = async (id) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.patch(`${API}/vehicles/${id}/toggle-active`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Vehicle ${response.data.is_active ? 'activated' : 'deactivated'}`);
      fetchVehicles();
    } catch (error) {
      toast.error('Failed to toggle status');
    }
  };

  const markAsSold = async (id) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.patch(`${API}/vehicles/${id}/mark-sold`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Vehicle ${response.data.is_sold ? 'marked as sold' : 'marked as available'}`);
      fetchVehicles();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleFeatureToggle = (feature) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter(f => f !== feature)
        : [...prev.features, feature]
    }));
  };

  // Image reorder functions
  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    const newImages = [...formData.images];
    const draggedItem = newImages[draggedIndex];
    newImages.splice(draggedIndex, 1);
    newImages.splice(index, 0, draggedItem);
    
    setFormData(prev => ({ ...prev, images: newImages }));
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  // Sort vehicles: active first, inactive, then sold at bottom
  const sortedVehicles = [...vehicles].sort((a, b) => {
    if (a.is_sold !== b.is_sold) return a.is_sold ? 1 : -1;
    if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
    return 0;
  });

  return (
    <div data-testid="inventory-management">
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <h1 className="text-2xl font-bold text-white">Inventory Management</h1>
        <div className="flex gap-3">
          <button
            onClick={() => setShowImportModal(true)}
            className="btn-primary inline-flex items-center gap-2"
            data-testid="import-cargurus-btn"
          >
            <Download size={20} />
            Import Inventory
          </button>
          <button
            onClick={() => { resetForm(); setEditingVehicle(null); setShowForm(true); }}
            className="btn-accent inline-flex items-center gap-2"
            data-testid="add-vehicle-btn"
          >
            <Plus size={20} />
            Add Vehicle
          </button>
        </div>
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-[#132238] rounded-xl p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Download className="text-[#ff6b35]" size={24} />
                Import Inventory
              </h2>
              <button onClick={() => setShowImportModal(false)} className="text-[#a8b8d0] hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            {/* Import Mode Tabs */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setImportMode('csv')}
                className={`flex-1 py-2 px-4 rounded-lg font-medium flex items-center justify-center gap-2 ${
                  importMode === 'csv' 
                    ? 'bg-[#ff6b35] text-white' 
                    : 'bg-[#1a2d47] text-[#a8b8d0] hover:text-white'
                }`}
              >
                <FileSpreadsheet size={18} />
                CSV Upload
              </button>
              <button
                onClick={() => setImportMode('scrape')}
                className={`flex-1 py-2 px-4 rounded-lg font-medium flex items-center justify-center gap-2 ${
                  importMode === 'scrape' 
                    ? 'bg-[#ff6b35] text-white' 
                    : 'bg-[#1a2d47] text-[#a8b8d0] hover:text-white'
                }`}
              >
                <Download size={18} />
                Web Scrape
              </button>
            </div>
            
            <div className="space-y-4">
              {/* CSV Upload Mode */}
              {importMode === 'csv' && (
                <>
                  <div className="bg-[#1a2d47]/50 rounded-lg p-4">
                    <p className="text-white font-medium mb-2">Upload CSV File</p>
                    <p className="text-[#a8b8d0] text-sm mb-3">
                      Export your inventory from CarGurus, Dealer Center, or any system as CSV.
                    </p>
                    <input
                      ref={csvInputRef}
                      type="file"
                      accept=".csv"
                      onChange={handleCsvUpload}
                      className="hidden"
                      id="csv-upload"
                    />
                    <label
                      htmlFor="csv-upload"
                      className={`btn-primary inline-flex items-center gap-2 cursor-pointer ${importing ? 'opacity-50 pointer-events-none' : ''}`}
                    >
                      {importing ? (
                        <>
                          <RefreshCw size={18} className="animate-spin" />
                          Importing...
                        </>
                      ) : (
                        <>
                          <Upload size={18} />
                          Choose CSV File
                        </>
                      )}
                    </label>
                  </div>
                  
                  <label className="flex items-center gap-2 text-white cursor-pointer">
                    <Checkbox
                      checked={replaceExisting}
                      onCheckedChange={(c) => setReplaceExisting(c)}
                      className="border-[#1a4d8c] data-[state=checked]:bg-[#ff6b35]"
                    />
                    <span>Update existing vehicles (match by VIN)</span>
                  </label>
                  
                  <div className="bg-[#0a1628] rounded-lg p-4 text-sm">
                    <p className="text-white font-medium mb-2">Required CSV Columns:</p>
                    <p className="text-[#a8b8d0] text-xs font-mono">
                      year, make, model, price, mileage
                    </p>
                    <p className="text-white font-medium mt-3 mb-2">Optional Columns:</p>
                    <p className="text-[#a8b8d0] text-xs font-mono">
                      vin, trim, body_type, drivetrain, engine, transmission, exterior_color, interior_color, description, images (comma-separated URLs)
                    </p>
                  </div>
                </>
              )}
              
              {/* Web Scrape Mode */}
              {importMode === 'scrape' && (
                <>
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-sm text-yellow-400">
                    ⚠️ Web scraping may have accuracy issues due to anti-bot protection. CSV upload is more reliable.
                  </div>
                  
                  <div>
                    <label className="text-[#a8b8d0] text-sm mb-1 block">CarGurus Dealer Page URL</label>
                    <input
                      type="url"
                      value={importUrl}
                      onChange={(e) => setImportUrl(e.target.value)}
                      placeholder="https://www.cargurus.com/Cars/m-Dealer-Name-sp123456"
                      className="w-full bg-[#1a2d47] border border-[#1a4d8c] rounded-lg px-4 py-2 text-white"
                    />
                  </div>
                  
                  <label className="flex items-center gap-2 text-white cursor-pointer">
                    <Checkbox
                      checked={replaceExisting}
                      onCheckedChange={(c) => setReplaceExisting(c)}
                      className="border-[#1a4d8c] data-[state=checked]:bg-[#ff6b35]"
                    />
                    <span>Update existing vehicles</span>
                  </label>
                  
                  <div className="flex gap-4 justify-end pt-4 border-t border-[#1a2d47]">
                    <button
                      onClick={handleImportCarGurus}
                      disabled={importing || !importUrl}
                      className="btn-accent inline-flex items-center gap-2 disabled:opacity-50"
                    >
                      {importing ? (
                        <>
                          <RefreshCw size={18} className="animate-spin" />
                          Scraping...
                        </>
                      ) : (
                        <>
                          <Download size={18} />
                          Start Scrape
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Vehicle Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-[#132238] rounded-xl p-6 w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white">
                {editingVehicle ? 'Edit Vehicle' : 'Add New Vehicle'}
              </h2>
              <button onClick={() => { setShowForm(false); setEditingVehicle(null); }} className="text-[#a8b8d0] hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div>
                <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                  <Car size={18} className="text-[#ff6b35]" />
                  Basic Information
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <input type="number" placeholder="Year *" value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: e.target.value })} required
                    className="bg-[#1a2d47] border border-[#1a4d8c] rounded-lg px-4 py-2 text-white" />
                  <input type="text" placeholder="Make *" value={formData.make}
                    onChange={(e) => setFormData({ ...formData, make: e.target.value })} required
                    className="bg-[#1a2d47] border border-[#1a4d8c] rounded-lg px-4 py-2 text-white" />
                  <input type="text" placeholder="Model *" value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })} required
                    className="bg-[#1a2d47] border border-[#1a4d8c] rounded-lg px-4 py-2 text-white" />
                  <input type="text" placeholder="Trim" value={formData.trim}
                    onChange={(e) => setFormData({ ...formData, trim: e.target.value })}
                    className="bg-[#1a2d47] border border-[#1a4d8c] rounded-lg px-4 py-2 text-white" />
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a8b8d0]" size={16} />
                    <input type="number" placeholder="Price *" value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })} required
                      className="bg-[#1a2d47] border border-[#1a4d8c] rounded-lg pl-8 pr-4 py-2 text-white w-full" />
                  </div>
                  <input type="number" placeholder="Mileage *" value={formData.mileage}
                    onChange={(e) => setFormData({ ...formData, mileage: e.target.value })} required
                    className="bg-[#1a2d47] border border-[#1a4d8c] rounded-lg px-4 py-2 text-white" />
                  <select value={formData.body_type}
                    onChange={(e) => setFormData({ ...formData, body_type: e.target.value })}
                    className="bg-[#1a2d47] border border-[#1a4d8c] rounded-lg px-4 py-2 text-white">
                    <option value="">Body Type</option>
                    {BODY_TYPE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                  <select value={formData.drivetrain}
                    onChange={(e) => setFormData({ ...formData, drivetrain: e.target.value })}
                    className="bg-[#1a2d47] border border-[#1a4d8c] rounded-lg px-4 py-2 text-white">
                    <option value="">Drivetrain</option>
                    <option value="FWD">FWD</option>
                    <option value="RWD">RWD</option>
                    <option value="4WD">4WD</option>
                    <option value="AWD">AWD</option>
                  </select>
                </div>
              </div>

              {/* Additional Details */}
              <div>
                <h3 className="text-white font-medium mb-3">Additional Details</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <input type="text" placeholder="Engine" value={formData.engine}
                    onChange={(e) => setFormData({ ...formData, engine: e.target.value })}
                    className="bg-[#1a2d47] border border-[#1a4d8c] rounded-lg px-4 py-2 text-white" />
                  <input type="text" placeholder="Transmission" value={formData.transmission}
                    onChange={(e) => setFormData({ ...formData, transmission: e.target.value })}
                    className="bg-[#1a2d47] border border-[#1a4d8c] rounded-lg px-4 py-2 text-white" />
                  <input type="text" placeholder="Exterior Color" value={formData.exterior_color}
                    onChange={(e) => setFormData({ ...formData, exterior_color: e.target.value })}
                    className="bg-[#1a2d47] border border-[#1a4d8c] rounded-lg px-4 py-2 text-white" />
                  <input type="text" placeholder="Interior Color" value={formData.interior_color}
                    onChange={(e) => setFormData({ ...formData, interior_color: e.target.value })}
                    className="bg-[#1a2d47] border border-[#1a4d8c] rounded-lg px-4 py-2 text-white" />
                  <input type="text" placeholder="VIN" value={formData.vin}
                    onChange={(e) => setFormData({ ...formData, vin: e.target.value })}
                    className="bg-[#1a2d47] border border-[#1a4d8c] rounded-lg px-4 py-2 text-white" />
                  <input type="text" placeholder="Stock #" value={formData.stock_number}
                    onChange={(e) => setFormData({ ...formData, stock_number: e.target.value })}
                    className="bg-[#1a2d47] border border-[#1a4d8c] rounded-lg px-4 py-2 text-white" />
                </div>
              </div>

              {/* Vehicle History */}
              <div className="bg-[#1a4d8c]/20 rounded-lg p-4 border border-[#1a4d8c]">
                <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                  <Shield size={18} className="text-[#22c55e]" />
                  Vehicle History
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="text-[#a8b8d0] text-xs mb-1 block">Title Status</label>
                    <select value={formData.title_status}
                      onChange={(e) => setFormData({ ...formData, title_status: e.target.value })}
                      className="w-full bg-[#1a2d47] border border-[#1a4d8c] rounded-lg px-3 py-2 text-white text-sm">
                      <option value="">Select...</option>
                      {TITLE_STATUS_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[#a8b8d0] text-xs mb-1 block">Accidents</label>
                    <select value={formData.accidents}
                      onChange={(e) => setFormData({ ...formData, accidents: e.target.value })}
                      className="w-full bg-[#1a2d47] border border-[#1a4d8c] rounded-lg px-3 py-2 text-white text-sm">
                      <option value="">Select...</option>
                      {ACCIDENT_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[#a8b8d0] text-xs mb-1 block">Previous Owners</label>
                    <select value={formData.previous_owners}
                      onChange={(e) => setFormData({ ...formData, previous_owners: e.target.value })}
                      className="w-full bg-[#1a2d47] border border-[#1a4d8c] rounded-lg px-3 py-2 text-white text-sm">
                      <option value="">Select...</option>
                      {OWNER_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[#a8b8d0] text-xs mb-1 block">Use Type</label>
                    <select value={formData.use_type}
                      onChange={(e) => setFormData({ ...formData, use_type: e.target.value })}
                      className="w-full bg-[#1a2d47] border border-[#1a4d8c] rounded-lg px-3 py-2 text-white text-sm">
                      <option value="">Select...</option>
                      {USE_TYPE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Features Checkboxes */}
              <div className="bg-[#1a2d47]/50 rounded-lg p-4">
                <label className="text-[#a8b8d0] text-sm font-medium block mb-3">Features</label>
                <div className="flex flex-wrap gap-4">
                  {ALLOWED_FEATURES.map(feature => (
                    <label key={feature} className="flex items-center gap-2 cursor-pointer text-white">
                      <Checkbox
                        checked={formData.features.includes(feature)}
                        onCheckedChange={() => handleFeatureToggle(feature)}
                        className="border-[#1a4d8c] data-[state=checked]:bg-[#ff6b35]"
                      />
                      <span className="text-sm">{feature}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Image Management */}
              <div className="bg-[#1a2d47]/50 rounded-lg p-4 border border-dashed border-[#1a4d8c]">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-[#a8b8d0] text-sm font-medium flex items-center gap-2">
                    <ImageIcon size={16} className="text-[#ff6b35]" />
                    Vehicle Images
                    <span className="text-xs text-[#a8b8d0]">(Drag to reorder - first image is thumbnail)</span>
                  </label>
                  <div className="flex gap-2">
                    <input type="file" ref={fileInputRef} onChange={handleImageUpload}
                      accept="image/*" multiple className="hidden" id="image-upload" />
                    <label htmlFor="image-upload"
                      className={`cursor-pointer inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        uploading ? 'bg-[#1a4d8c]/50 text-[#a8b8d0] cursor-not-allowed' : 'bg-[#ff6b35] hover:bg-[#e55a2b] text-white'
                      }`}>
                      {uploading ? <><RefreshCw size={14} className="animate-spin" />Uploading...</> : <><Upload size={14} />Upload</>}
                    </label>
                  </div>
                </div>
                
                {/* Image Grid */}
                {formData.images.length > 0 ? (
                  <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                    {formData.images.map((img, index) => (
                      <div
                        key={index}
                        draggable
                        onDragStart={() => handleDragStart(index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragEnd={handleDragEnd}
                        className={`relative group aspect-square rounded-lg overflow-hidden border-2 cursor-move ${
                          index === 0 ? 'border-[#ff6b35]' : 'border-[#1a4d8c]'
                        } ${draggedIndex === index ? 'opacity-50' : ''}`}
                      >
                        <img src={img} alt="" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <GripVertical className="text-white" size={20} />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-1 right-1 p-1 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X size={12} className="text-white" />
                        </button>
                        {index === 0 && (
                          <span className="absolute bottom-1 left-1 bg-[#ff6b35] text-white text-xs px-1.5 py-0.5 rounded">
                            Thumbnail
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-[#a8b8d0]">
                    <ImageIcon size={48} className="mx-auto mb-2 opacity-50" />
                    <p>No images uploaded yet</p>
                    <p className="text-xs">Upload images or they'll show "Pictures Coming Soon"</p>
                  </div>
                )}
              </div>

              {/* Vehicle Description */}
              <div className="space-y-4">
                <div>
                  <label className="text-[#a8b8d0] text-sm font-medium mb-2 block">Vehicle Description</label>
                  <p className="text-[#64748b] text-xs mb-2">This is the main description shown on the vehicle detail page. Edit directly or use AI to generate.</p>
                  <textarea placeholder="Vehicle description shown on the listing page..." value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={4}
                    className="w-full bg-[#0a1628] border border-[#1a2d47] rounded-lg px-4 py-2 text-white text-sm" />
                </div>
                
                {/* AI Description Generator */}
                <div className="bg-gradient-to-r from-[#1a4d8c]/30 to-[#ff6b35]/20 rounded-lg p-4 border border-[#1a4d8c]">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-white text-sm font-medium flex items-center gap-2">
                      <Sparkles size={16} className="text-[#ff6b35]" />
                      We Have Your Car AI Tool
                    </label>
                    <button type="button" onClick={handleGenerateDescription} disabled={generatingDesc}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        generatingDesc ? 'bg-[#1a4d8c]/50 text-[#a8b8d0]' : 'bg-[#ff6b35] hover:bg-[#e55a2b] text-white'
                      }`}>
                      {generatingDesc ? <><RefreshCw size={14} className="animate-spin" />Generating...</> : <><Sparkles size={14} />Generate with AI</>}
                    </button>
                  </div>
                  <p className="text-[#a8b8d0] text-xs mb-2">AI generates a description and copies it to the main description field above.</p>
                  <textarea placeholder="AI-generated description will appear here..." value={formData.ai_description}
                    onChange={(e) => setFormData({ ...formData, ai_description: e.target.value })} rows={3}
                    className="w-full bg-[#0a1628] border border-[#1a4d8c] rounded-lg px-4 py-2 text-white text-sm" />
                </div>
              </div>

              {/* CARFAX Section */}
              <div className="bg-[#1a2d47]/50 rounded-lg p-4">
                <label className="text-[#a8b8d0] text-sm font-medium flex items-center gap-2 mb-3">
                  <FileText size={16} className="text-[#ff6b35]" />
                  CARFAX Report
                </label>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <input type="url" placeholder="CARFAX URL (link)" value={formData.carfax_url}
                      onChange={(e) => setFormData({ ...formData, carfax_url: e.target.value })}
                      className="w-full bg-[#0a1628] border border-[#1a4d8c] rounded-lg px-4 py-2 text-white text-sm" />
                  </div>
                  <div className="flex gap-2 items-center">
                    <input type="file" ref={carfaxInputRef} onChange={handleCarfaxUpload}
                      accept="application/pdf" className="hidden" id="carfax-upload" />
                    <label htmlFor="carfax-upload"
                      className={`cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        uploadingCarfax || !editingVehicle ? 'bg-[#1a4d8c]/50 text-[#a8b8d0]' : 'bg-[#1a4d8c] hover:bg-[#1a5d9c] text-white'
                      }`}>
                      {uploadingCarfax ? 'Uploading...' : 'Upload PDF'}
                    </label>
                    <label className="flex items-center gap-2 text-white text-sm">
                      <Checkbox checked={formData.show_carfax}
                        onCheckedChange={(c) => setFormData({ ...formData, show_carfax: c })}
                        className="border-[#1a4d8c] data-[state=checked]:bg-[#ff6b35]" />
                      Show CARFAX
                    </label>
                  </div>
                </div>
                {formData.carfax_file && <p className="text-[#22c55e] text-xs mt-2">PDF uploaded ✓</p>}
              </div>

              {/* SEO Section */}
              <div className="bg-[#1a2d47]/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-white font-medium">SEO Settings (Optional)</h3>
                  <button
                    type="button"
                    onClick={generateSeo}
                    disabled={generatingSeo}
                    className="px-3 py-1.5 bg-[#1a4d8c] text-white rounded-lg text-sm hover:bg-[#1a4d8c]/80 disabled:opacity-50 flex items-center gap-1.5"
                  >
                    <Sparkles size={14} className={generatingSeo ? 'animate-spin' : ''} />
                    {generatingSeo ? 'Generating...' : 'AI Generate SEO'}
                  </button>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="text-[#a8b8d0] text-xs mb-1 block">Page Title (max 60 chars)</label>
                    <input type="text" placeholder="e.g., 2020 Toyota Camry | We Have Your Car" 
                      value={formData.seo_title}
                      onChange={(e) => setFormData({ ...formData, seo_title: e.target.value })}
                      maxLength={60}
                      className="w-full bg-[#0a1628] border border-[#1a4d8c] rounded-lg px-4 py-2 text-white text-sm" />
                    <p className="text-[#a8b8d0] text-xs mt-1">{formData.seo_title?.length || 0}/60 characters</p>
                  </div>
                  <div>
                    <label className="text-[#a8b8d0] text-xs mb-1 block">Meta Description (max 155 chars)</label>
                    <textarea placeholder="Meta Description for search engines" 
                      value={formData.seo_description}
                      onChange={(e) => setFormData({ ...formData, seo_description: e.target.value })} 
                      rows={2}
                      maxLength={155}
                      className="w-full bg-[#0a1628] border border-[#1a4d8c] rounded-lg px-4 py-2 text-white text-sm" />
                    <p className="text-[#a8b8d0] text-xs mt-1">{formData.seo_description?.length || 0}/155 characters</p>
                  </div>
                </div>
              </div>

              {/* Status toggles */}
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center gap-2 text-white">
                  <Checkbox checked={formData.is_active}
                    onCheckedChange={(c) => setFormData({ ...formData, is_active: c })}
                    className="border-[#1a4d8c] data-[state=checked]:bg-[#22c55e]" />
                  Active Listing
                </label>
                <label className="flex items-center gap-2 text-white">
                  <Checkbox checked={formData.is_featured}
                    onCheckedChange={(c) => setFormData({ ...formData, is_featured: c })}
                    className="border-[#1a4d8c] data-[state=checked]:bg-[#ff6b35]" />
                  Featured Vehicle
                </label>
                <label className="flex items-center gap-2 text-white">
                  <Checkbox checked={formData.is_sold}
                    onCheckedChange={(c) => setFormData({ ...formData, is_sold: c, is_active: c ? false : formData.is_active })}
                    className="border-[#1a4d8c] data-[state=checked]:bg-[#ef4444]" />
                  Sold
                </label>
              </div>
              
              {/* Form Actions */}
              <div className="flex gap-4 justify-end pt-4 border-t border-[#1a2d47]">
                <button type="button" onClick={() => { setShowForm(false); setEditingVehicle(null); }}
                  className="px-4 py-2 text-[#a8b8d0] hover:text-white">Cancel</button>
                <button type="submit" className="btn-accent">{editingVehicle ? 'Update' : 'Add'} Vehicle</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Vehicles Table */}
      <div className="bg-[#132238] rounded-xl border border-[#1a2d47] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#1a2d47]">
              <tr>
                <th className="text-left text-[#a8b8d0] px-4 py-3 text-sm font-medium">Vehicle</th>
                <th className="text-left text-[#a8b8d0] px-4 py-3 text-sm font-medium">Price</th>
                <th className="text-left text-[#a8b8d0] px-4 py-3 text-sm font-medium">Mileage</th>
                <th className="text-left text-[#a8b8d0] px-4 py-3 text-sm font-medium">Views</th>
                <th className="text-left text-[#a8b8d0] px-4 py-3 text-sm font-medium">Status</th>
                <th className="text-left text-[#a8b8d0] px-4 py-3 text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedVehicles.map((vehicle) => (
                <tr key={vehicle.id} className={`border-t border-[#1a2d47] ${vehicle.is_sold ? 'opacity-60' : ''}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {vehicle.images?.[0] ? (
                        <img src={vehicle.images[0]} alt="" className="w-12 h-12 object-cover rounded"
                          onError={(e) => e.target.style.display = 'none'} />
                      ) : (
                        <div className="w-12 h-12 bg-[#1a2d47] rounded flex items-center justify-center">
                          <ImageIcon size={16} className="text-[#a8b8d0]" />
                        </div>
                      )}
                      <div>
                        <p className="text-white font-medium">{vehicle.year} {vehicle.make}</p>
                        <p className="text-[#a8b8d0] text-sm">{vehicle.model}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-white">${vehicle.price?.toLocaleString()}</td>
                  <td className="px-4 py-3 text-[#a8b8d0]">{vehicle.mileage?.toLocaleString()} mi</td>
                  <td className="px-4 py-3 text-[#a8b8d0]">{vehicle.view_count || 0}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      {vehicle.is_sold ? (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-red-500/20 text-red-400 w-fit">Sold</span>
                      ) : (
                        <span className={`px-2 py-0.5 rounded-full text-xs w-fit ${
                          vehicle.is_active ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {vehicle.is_active ? 'Active' : 'Inactive'}
                        </span>
                      )}
                      {vehicle.is_featured && (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-[#ff6b35]/20 text-[#ff6b35] w-fit">Featured</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => toggleActive(vehicle.id)}
                        className="p-2 text-[#a8b8d0] hover:text-white hover:bg-[#1a2d47] rounded"
                        title={vehicle.is_active ? 'Deactivate' : 'Activate'}>
                        {vehicle.is_active ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                      <button onClick={() => markAsSold(vehicle.id)}
                        className={`p-2 hover:bg-[#1a2d47] rounded ${vehicle.is_sold ? 'text-green-400' : 'text-[#a8b8d0] hover:text-white'}`}
                        title={vehicle.is_sold ? 'Mark Available' : 'Mark Sold'}>
                        {vehicle.is_sold ? <Check size={16} /> : <DollarSign size={16} />}
                      </button>
                      <button onClick={() => handleEdit(vehicle)}
                        className="p-2 text-[#a8b8d0] hover:text-white hover:bg-[#1a2d47] rounded">
                        <Edit size={16} />
                      </button>
                      <button onClick={() => handleDelete(vehicle.id)}
                        className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default InventoryManagement;
